<template>
  <v-app>
    <!-- Barra de navegação superior fixa -->
    <v-app-bar app color="primary" dark elevation="4">
      <div class="d-flex align-center">
        <v-img
          alt="QuarnX Logo"
          class="shrink mr-2"
          contain
          :src="'/logo.svg'"
          transition="scale-transition"
          width="40"
        />

        <h1 class="text-h5 font-weight-bold">
          QuarnX Explorer
        </h1>
      </div>

      <v-spacer></v-spacer>

      <!-- Menu de navegação principal -->
      <v-tabs
        v-model="activeTab"
        align-with-title
        hide-slider
      >
        <v-tab 
          v-for="item in translatedMenuItems.filter(item => item.path !== '/dex')" 
          :key="item.path" 
          :to="item.path"
          :value="item.path"
          class="text-subtitle-1"
        >
          <v-icon start>{{ item.icon }}</v-icon>
          {{ item.title }}
        </v-tab>
      </v-tabs>

      <v-spacer></v-spacer>

      <!-- Botão de busca universal -->
      <v-btn icon @click="toggleSearch">
        <v-icon>mdi-magnify</v-icon>
      </v-btn>

      <!-- Botão de Carteira -->
      <wallet-button class="mr-2" />

      <!-- Seletor de Idioma -->
      <language-selector class="mr-2" />

      <!-- Menu de configurações -->
      <v-menu
        v-model="userMenu"
        :close-on-content-click="false"
        location="bottom"
      >
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props">
            <v-icon>mdi-cog</v-icon>
          </v-btn>
        </template>

        <v-card min-width="300">
          <v-list>
            <v-list-item prepend-icon="mdi-theme-light-dark" :title="t('settings.toggleTheme')"@click="toggleTheme"></v-list-item>
            <v-list-item prepend-icon="mdi-refresh" :title="t('settings.refreshCache')"@click="refreshCache"></v-list-item>
            <v-list-item prepend-icon="mdi-information-outline" :title="t('settings.about')"@click="showAbout = true"></v-list-item>
          </v-list>
        </v-card>
      </v-menu>
    </v-app-bar>

    <!-- Barra de busca universal (exibida quando ativa) -->
    <v-expand-transition>
      <v-sheet v-if="searchActive" class="search-bar" color="surface" elevation="4">
        <v-container>
          <v-row align="center">
            <v-col cols="12">
              <v-text-field
                v-model="searchQuery"
                label="Buscar bloco, transação, endereço ou token..."
                variant="outlined"
                hide-details
                append-icon="mdi-close"
                @click:append="toggleSearch"
                @keydown.enter="performSearch"
                autofocus
              ></v-text-field>
            </v-col>
          </v-row>
        </v-container>
      </v-sheet>
    </v-expand-transition>

    <!-- Container principal para conteúdo dinâmico -->
    <v-main>
      <!-- Indicador de desconexão WebSocket -->
      <v-alert
        v-if="!wsConnected"
        type="warning"
        variant="tonal"
        density="compact"
        closable
        class="ma-2"
        icon="mdi-connection"
      >
        Desconectado — atualizações em tempo real indisponíveis
      </v-alert>
      <!-- Exibe notificações do sistema -->
      <v-snackbar
        v-model="notification.active"
        :color="notification.color"
        :timeout="notification.timeout"
      >
        {{ notification.text }}

        <template v-slot:actions>
          <v-btn
            variant="text"
            @click="notification.active = false"
          >
            Fechar
          </v-btn>
        </template>
      </v-snackbar>

      <!-- Diálogo "Sobre" -->
      <v-dialog v-model="showAbout" width="600">
        <v-card>
          <v-card-title class="text-h5">
            Sobre o QuarnX Explorer
          </v-card-title>
          <v-card-text>
            <p class="mb-2">Explorador de blocos para a blockchain QuarnX, baseada em PoSEM (Proof of Stake and Ecosystem Merit).</p>
            <p class="mb-2">Versão: 0.1.0</p>
            <p>Desenvolvido pela equipe QuarnX.</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              variant="text"
              @click="showAbout = false"
            >
              Fechar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Router view para conteúdo dinâmico -->
      <v-container fluid>
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </v-container>
    </v-main>

    <!-- Rodapé com dados da rede ao vivo -->
    <v-footer app color="primary" class="px-4 py-2">
      <div class="d-flex align-center flex-wrap" style="gap: 16px; width: 100%;">
        <span class="text-caption text-white">QuarnX Explorer &copy; {{ new Date().getFullYear() }}</span>
        <v-spacer></v-spacer>
        <div class="d-flex align-center" style="gap: 12px;">
          <v-chip size="x-small" :color="networkStatus.color" variant="flat" label>
            <v-icon start size="x-small">{{ networkStatus.icon }}</v-icon>
            {{ appStore.networkStatus.networkName || 'QuarnX' }}
          </v-chip>
          <span class="text-caption text-white" v-if="appStore.currentBlockHeight > 0">
            Bloco #{{ appStore.currentBlockHeight.toLocaleString() }}
          </span>
          <span class="text-caption text-white" v-if="appStore.networkStatus.tps > 0">
            {{ appStore.networkStatus.tps.toFixed(1) }} TPS
          </span>
          <span class="text-caption text-white">
            Chain {{ appStore.networkStatus.chainId }}
          </span>
          <v-icon
            v-if="wsConnected"
            size="x-small"
            color="green-accent-3"
            class="ws-pulse"
          >mdi-circle</v-icon>
          <v-icon
            v-else
            size="x-small"
            color="red"
          >mdi-circle-outline</v-icon>
        </div>
      </div>
    </v-footer>
  </v-app>
</template>

<script>
import WalletButton from '@/components/common/WalletButton.vue'
import LanguageSelector from '@/components/common/LanguageSelector.vue'
import { useLocaleStore } from '@/stores/locale'
import { useBlockchainStore } from '@/stores/blockchain'
import { useAppStore } from '@/stores/app'

export default {
  name: 'App',
  components: {
    WalletButton,
    LanguageSelector
  },
  computed: {
    wsConnected() {
      const blockchainStore = useBlockchainStore()
      return blockchainStore.wsConnected
    },
    appStore() {
      return useAppStore()
    },
    translatedMenuItems() {
      const localeStore = useLocaleStore()
      return [
        { title: localeStore.t('nav.dashboard'), path: '/', icon: 'mdi-view-dashboard' },
        { title: localeStore.t('nav.blocks'), path: '/blocks', icon: 'mdi-cube-outline' },
        { title: localeStore.t('nav.transactions'), path: '/transactions', icon: 'mdi-swap-horizontal' },
        { title: localeStore.t('nav.accounts'), path: '/accounts', icon: 'mdi-account-outline' },
        { title: localeStore.t('nav.validators'), path: '/validators', icon: 'mdi-check-network-outline' },
        { title: localeStore.t('nav.developer'), path: '/developer', icon: 'mdi-code-braces' },
        { title: localeStore.t('sale.buy'), path: '/token-sale', icon: 'mdi-cart' },
        { title: 'DEX', path: '/dex', icon: 'mdi-chart-line' },
        { title: 'QuarnXSwap', path: '/quarnxswap', icon: 'mdi-swap-vertical-circle' }
      ]
    }
  },
  data() {
    return {
      activeTab: null,
      searchActive: false,
      searchQuery: '',
      userMenu: false,
      showAbout: false,
      notification: {
        active: false,
        text: '',
        color: 'info',
        timeout: 5000
      },
      menuItems: [],
      networkStatus: {
        text: 'Conectando...',
        color: 'warning',
        icon: 'mdi-loading'
      }
    }
  },
  methods: {
    toggleSearch() {
      this.searchActive = !this.searchActive
      if (!this.searchActive) {
        this.searchQuery = ''
      }
    },
    performSearch() {
      if (!this.searchQuery.trim()) return
      
      // Lógica para determinar o tipo de busca (bloco, transação, endereço)
      // Exemplo simplificado:
      const query = this.searchQuery.trim()
      
      if (/^\d+$/.test(query)) {
        // Se for apenas números, provavelmente é um bloco
        this.$router.push({ path: `/blocks/${query}` })
      } else if (query.startsWith('0x') && query.length === 66) {
        // Se parece um hash de transação (0x seguido de 64 caracteres)
        this.$router.push({ path: `/transactions/${query}` })
      } else if (query.startsWith('0x') && query.length === 42) {
        // Se parece um endereço (0x seguido de 40 caracteres)
        this.$router.push({ path: `/accounts/${query}` })
      } else {
        // Busca genérica
        this.$router.push({ path: '/search', query: { q: query } })
      }
      
      this.toggleSearch()
    },
    toggleTheme() {
      // Alterna entre tema claro e escuro
      this.$vuetify.theme.global.name = 
        this.$vuetify.theme.global.current.dark ? 'temaQuarnXClaro' : 'temaQuarnX'
      this.userMenu = false
    },
    refreshCache() {
      // Lógica para atualizar o cache
      this.showNotification('Cache atualizado com sucesso!', 'success')
      this.userMenu = false
    },
    showNotification(text, color = 'info', timeout = 5000) {
      this.notification = {
        active: true,
        text,
        color,
        timeout
      }
    }
  },
  async mounted() {
    // Inicializar dados e WebSocket
    const blockchainStore = useBlockchainStore()
    await blockchainStore.initializeData()
    
    // Set global coin symbol from network stats for utility functions
    const appStore = useAppStore()
    window.__QUARNX_COIN__ = appStore.coinSymbol
    
    // Atualizar status da rede
    try {
      const { API_BASE_URL } = await import('@/services/api')
      const resp = await fetch(`${API_BASE_URL}/health`)
      const data = await resp.json()
      if (data.status === 'ok') {
        this.networkStatus = { text: 'Sincronizado', color: 'success', icon: 'mdi-check-circle' }
      }
    } catch {
      this.networkStatus = { text: 'Desconectado', color: 'error', icon: 'mdi-alert-circle' }
    }

    // Auto-refresh stats every 30s
    this._refreshInterval = setInterval(() => {
      blockchainStore.updateBlockchainStats()
    }, 30000)
  },
  beforeUnmount() {
    if (this._refreshInterval) clearInterval(this._refreshInterval)
  }
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.ws-pulse {
  animation: ws-blink 2s infinite;
}

@keyframes ws-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.search-bar {
  position: relative;
  z-index: 4;
  padding: 8px 0;
}
</style>
